import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { useAccount, UserAccount } from '../../context/AccountContext';
import { GET_ACCOUNT_BILLING_DETAILS } from '../../graphql';
import { CREATE_SETUP_INTENT, UPDATE_PUBLIC_SITE } from '../../graphql/mutations';
import {
  BillingInterval,
  chargeLine,
  money,
  planPrice,
  TinycmsPlan,
  useTinycmsPlans,
} from '../account/tinycmsPlans';
import { showInkToast } from './InkToast';
import { CREATE_TINYCMS_SITE, SiteSummary } from './sites';

/**
 * New site ("tinycms UX optimization review v6.zip", Prototype v3): signup's
 * tail for someone already signed in. Plan → Address → Agent, then the site
 * (an account) is created, the app switches to it, and either the agent setup
 * opens (/signup resumes there for a site with an address and no agent yet) or
 * Today does.
 *
 * A paid plan bills the card already on the current site when the user owns
 * it; otherwise the last step asks for one. There's no free trial here: each
 * person gets one, with the site they signed up with.
 */

type Step = 'plan' | 'address' | 'agent';
const STEPS: Array<[Step, string]> = [
  ['plan', 'Plan'],
  ['address', 'Address'],
  ['agent', 'Agent'],
];

const SLUG_AVAILABLE = gql`
  query NewSiteSlugAvailable($slug: String!) {
    publicSlugAvailable(slug: $slug) {
      slug
      available
      reason
    }
  }
`;

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;

const stripePromise: Promise<Stripe | null> = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);
const isStripeTestMode =
  !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_');

function toSlug(name: string) {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

export function NewSiteModal(props: { sites: SiteSummary[]; onClose: () => void }) {
  return (
    <Elements stripe={stripePromise}>
      <NewSite {...props} />
    </Elements>
  );
}

function NewSite({ sites, onClose }: { sites: SiteSummary[]; onClose: () => void }) {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { selectedAccount, setSelectedAccount, refetchAccounts } = useAccount();
  const { plans } = useTinycmsPlans();

  const [step, setStep] = useState<Step>('plan');
  const [planKey, setPlanKey] = useState('personal');
  const [interval, setInterval] = useState<BillingInterval>('year');
  const [name, setName] = useState('');
  const [slugTyped, setSlugTyped] = useState<string | null>(null);
  const [agent, setAgent] = useState<'now' | 'later'>('now');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const plan: TinycmsPlan | undefined = plans.find((p) => p.key === planKey);
  const price = plan ? planPrice(plan, interval) : 0;
  const paid = price > 0;
  const idx = STEPS.findIndex(([k]) => k === step);

  // The card on the site the user is in, when they own it.
  const here = sites.find((s) => s.accountId === selectedAccount?.id);
  const canShareCard = here?.role === 'admin';
  const { data: billing } = useQuery<{
    billingDetails: Array<{ cardLast4?: string; isDefault?: boolean }>;
  }>(GET_ACCOUNT_BILLING_DETAILS, {
    variables: { accountId: here?.accountId },
    skip: !canShareCard,
    errorPolicy: 'all',
  });
  const sharedCard = canShareCard
    ? (billing?.billingDetails || []).find((c) => c.isDefault)?.cardLast4 || null
    : null;
  const needsCard = paid && !sharedCard;

  // Address: from the name until the user types their own.
  const slug = slugTyped ?? toSlug(name);
  const [check, { data: availData, loading: checking }] = useLazyQuery(SLUG_AVAILABLE, {
    fetchPolicy: 'network-only',
  });
  useEffect(() => {
    if (!SLUG_RE.test(slug)) return;
    const t = setTimeout(() => void check({ variables: { slug } }), 300);
    return () => clearTimeout(t);
  }, [slug, check]);
  const avail = availData?.publicSlugAvailable;
  const current = avail && avail.slug === slug ? avail : null;
  const host = `${slug}.tinycms.ai`;
  let addrTone: 'ok' | 'error' | undefined;
  let addrLine: string;
  if (!slug) addrLine = 'Pick an address readers will type.';
  else if (slug.length < 2) addrLine = 'At least 2 characters.';
  else if (!SLUG_RE.test(slug)) addrLine = 'Start with a letter or number.';
  else if (!current || checking) addrLine = 'Checking…';
  else if (current.available) {
    addrTone = 'ok';
    addrLine = `✓ ${host} is available`;
  } else {
    addrTone = 'error';
    addrLine = current.reason === 'reserved' ? `${slug} is reserved.` : 'That address is taken.';
  }
  const addressOk = Boolean(name.trim()) && addrTone === 'ok';

  const [createSetupIntent] = useMutation<{ setupIntent: { clientSecret: string } }>(
    CREATE_SETUP_INTENT
  );
  const [createSite] = useMutation<{ createTinycmsSite: { id: string; name: string } }>(
    CREATE_TINYCMS_SITE
  );
  const [updatePublicSite] = useMutation(UPDATE_PUBLIC_SITE);

  const cardStyle = useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (n: string, fallback: string) => css.getPropertyValue(n).trim() || fallback;
    return {
      style: {
        base: {
          fontSize: '15px',
          color: v('--ink-ink', '#0d0f12'),
          fontFamily: 'Geist, system-ui, sans-serif',
          '::placeholder': { color: v('--ink-ink3', '#7a8190') },
        },
        invalid: { color: v('--ink-red', '#d33'), iconColor: v('--ink-red', '#d33') },
      },
      hidePostalCode: true,
    };
  }, []);

  // Esc closes, except mid-create.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  /** A new card, confirmed with Stripe; null (with the error shown) when it fails. */
  const confirmCard = async (): Promise<string | null> => {
    const card = elements?.getElement(CardElement);
    if (!stripe || !card) {
      setError('The card form hasn’t loaded yet. Try again in a moment.');
      return null;
    }
    const { data } = await createSetupIntent();
    const secret = data?.setupIntent?.clientSecret;
    if (!secret) {
      setError('Your card couldn’t be saved. Try again.');
      return null;
    }
    const { error: cardErr, setupIntent } = await stripe.confirmCardSetup(secret, {
      payment_method: { card },
    });
    if (cardErr || !setupIntent?.payment_method) {
      setError(cardErr?.message || 'Your card couldn’t be saved. Try again.');
      return null;
    }
    return setupIntent.payment_method as string;
  };

  const create = async () => {
    if (!plan) return;
    setBusy(true);
    setError('');
    const siteName = name.trim();
    try {
      const paymentMethod = needsCard ? await confirmCard() : null;
      if (needsCard && !paymentMethod) return;
      const { data } = await createSite({
        variables: {
          input: {
            name: siteName,
            plan: plan.key,
            billingInterval: interval,
            ...(paid && sharedCard ? { billingAccountId: here?.accountId } : {}),
            ...(paymentMethod ? { paymentMethod } : {}),
          },
        },
      });
      const id = data?.createTinycmsSite?.id;
      if (!id) throw new Error('The site wasn’t created. Try again.');

      // Switch the app to the new site.
      const result = (await refetchAccounts()) as {
        data?: { userAccounts?: UserAccount[] };
      };
      const account = result?.data?.userAccounts?.find((a) => a.id === id);
      if (account) setSelectedAccount(account);

      // Its address, as signup's Site address step saves it. If this fails the
      // site still exists, and the app sends it back to signup to pick one.
      let addressSaved = true;
      try {
        await updatePublicSite({
          variables: {
            accountId: id,
            input: { publicSlug: slug, publicTitle: siteName, publicEnabled: true },
          },
        });
      } catch {
        addressSaved = false;
      }

      onClose();
      if (!addressSaved) {
        showInkToast(`${siteName} is ready, but ${host} was just taken. Pick another address.`);
        navigate('/signup');
      } else if (agent === 'now') {
        navigate('/signup');
      } else {
        // Still picks a starting point (signup resumes there); skips the agent.
        showInkToast(`${siteName} is ready. Connect an agent any time in Settings → Connections.`);
        navigate('/signup?agent=later');
      }
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const ctaOn = step === 'address' ? addressOk : step === 'plan' ? Boolean(plan) : !busy;
  const next = () => {
    if (!ctaOn) return;
    setError('');
    if (step === 'plan') setStep('address');
    else if (step === 'address') setStep('agent');
    else void create();
  };

  const cta =
    step !== 'agent'
      ? 'Continue'
      : busy
        ? 'Creating…'
        : paid
          ? `Create site · ${chargeLine(plan!, interval)}`
          : 'Create site';

  return (
    <div
      className="ink-modal-back ns-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="ns" role="dialog" aria-modal="true" aria-labelledby="ns-title">
        <div className="ns-head">
          <div style={{ flex: 1 }}>
            <div className="ns-title" id="ns-title">
              New site
            </div>
            <ol className="ns-steps">
              {STEPS.map(([k, label], i) => (
                <li key={k} data-state={i < idx ? 'done' : i === idx ? 'now' : 'next'}>
                  <span className="ns-step-num">{i < idx ? '✓' : i + 1}</span>
                  {label}
                </li>
              ))}
            </ol>
          </div>
          <button
            type="button"
            className="cp-close"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="ns-body">
          {step === 'plan' ? (
            <>
              <div className="ns-lede">
                Choose a plan for this site. Your free trial came with your first site, so paid
                plans here start today.
              </div>
              <div className="cp-seg ns-seg" role="radiogroup" aria-label="Billing">
                {(['month', 'year'] as BillingInterval[]).map((v) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={interval === v}
                    data-active={interval === v ? 'true' : undefined}
                    onClick={() => setInterval(v)}
                  >
                    {v === 'month' ? 'Monthly' : 'Yearly'}
                    {v === 'year' ? <span className="cp-save">Save 25%</span> : null}
                  </button>
                ))}
              </div>
              <div className="ns-options" role="radiogroup" aria-label="Plan">
                {plans.map((p) => {
                  return (
                    <button
                      key={p.key}
                      type="button"
                      role="radio"
                      aria-checked={p.key === planKey}
                      className="ns-option"
                      data-on={p.key === planKey ? 'true' : undefined}
                      onClick={() => setPlanKey(p.key)}
                    >
                      <span className="ns-radio" />
                      <span className="ns-option-text">
                        <span className="ns-option-label">{p.label}</span>
                        <span className="ns-option-sub">{p.summary}</span>
                      </span>
                      <span className="ns-option-price">
                        <span>
                          {planPrice(p, interval) ? money(planPrice(p, interval)) : 'Free'}
                        </span>
                        <span className="ns-option-per">
                          {planPrice(p, interval)
                            ? `a month, billed ${interval === 'year' ? 'yearly' : 'monthly'}`
                            : 'no card needed'}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          {step === 'address' ? (
            <div className="ns-fields">
              <label className="ns-field">
                <span className="ns-label">Site name</span>
                <input
                  className="ns-input"
                  value={name}
                  autoFocus
                  placeholder="e.g. Rosa’s Bakery"
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="ns-field">
                <span className="ns-label">Address</span>
                <span className="ns-addr">
                  <input
                    value={slug}
                    placeholder="your-site"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    onChange={(e) =>
                      setSlugTyped(
                        e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, '')
                          .slice(0, 63)
                      )
                    }
                  />
                  <span>.tinycms.ai</span>
                </span>
                <span className="ns-avail" data-tone={addrTone}>
                  {addrLine}
                </span>
              </label>
              <div className="ns-note">You can connect your own domain once the site exists.</div>
            </div>
          ) : null}

          {step === 'agent' && plan ? (
            <>
              <div className="ns-lede">Connect an agent to write for this site?</div>
              <div className="ns-options" role="radiogroup" aria-label="Agent">
                {(
                  [
                    [
                      'now',
                      'Connect an agent now',
                      'Claude, ChatGPT or any MCP client. You’ll get this site’s server address and secret next.',
                    ],
                    [
                      'later',
                      'I’ll do it later',
                      'Each site has its own connection. Set it up any time in Settings → Connections.',
                    ],
                  ] as const
                ).map(([k, label, sub]) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={agent === k}
                    className="ns-option"
                    data-on={agent === k ? 'true' : undefined}
                    onClick={() => setAgent(k)}
                  >
                    <span className="ns-radio" />
                    <span className="ns-option-text">
                      <span className="ns-option-label">{label}</span>
                      <span className="ns-option-sub">{sub}</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="ns-summary">
                <div className="ns-summary-row">
                  <strong>{name.trim() || 'New site'}</strong>
                  <span className="ns-mono">{host}</span>
                </div>
                <div className="ns-summary-row">
                  <span>{plan.label} plan</span>
                  <span>{paid ? chargeLine(plan, interval) : 'Free'}</span>
                </div>
                {needsCard ? (
                  <div className="ns-card">
                    {isStripeTestMode ? (
                      <div className="ns-fine">
                        Test mode: 4242 4242 4242 4242, any date, any CVC.
                      </div>
                    ) : null}
                    <div className="flow-card">
                      <CardElement options={cardStyle} onChange={() => setError('')} />
                    </div>
                    <div className="ns-fine">
                      No free trial on a new site: billing starts today.
                    </div>
                  </div>
                ) : (
                  <div className="ns-fine">
                    {!paid
                      ? 'No card needed.'
                      : `No free trial on a new site: billing starts today, on the card ending ${sharedCard} (the one on ${here?.name}).`}
                  </div>
                )}
              </div>
            </>
          ) : null}
          {error ? <p className="ns-error">{error}</p> : null}
        </div>

        <div className="ns-foot">
          {idx > 0 ? (
            <button
              type="button"
              className="ns-back-btn"
              disabled={busy}
              onClick={() => {
                setError('');
                setStep(STEPS[idx - 1][0]);
              }}
            >
              ← Back
            </button>
          ) : null}
          <button
            type="button"
            className="ink-btn ink-btn-primary ns-cta"
            disabled={!ctaOn}
            onClick={next}
          >
            {cta}
          </button>
        </div>
      </div>
    </div>
  );
}
